import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, CardContent } from "./card";
import { Upload } from "lucide-react";
import { SharePointFileUpload } from "./sharepoint-file-upload";
import {
  WORKSPACE_DOCUMENT_CARDS,
  getWorkspacePrefix,
  type DocumentCard,
} from "../../utils/workspace-document-cards";

interface InformationIPEIUCSectionProps {
  workspaceName: string;
  workspaceId: string;
  area: "Planning" | "Execution" | "ConcludingProcedures";
  /** When set (e.g. FRF-specific cards), overrides WORKSPACE_DOCUMENT_CARDS for this workspace */
  documentCards?: DocumentCard[];
  onFilesUploaded?: () => void;
  onRemoveFile?: () => void;
  sharePointFiles?: Array<{ name: string }>;
  isLoadingSharePointFiles?: boolean;
  existingFiles?: Record<string, string>;
}

interface InformationEntry {
  information_name: string;
  workspace: string[];
  workspace_prefix: string[];
  file_name: string;
}

/**
 * Resolve linked file names from db.json (client folder) via SharePoint API —
 * same source uploads use when sectionList is applied.
 */
async function fetchFileNamesFromDbBySectionCodes(
  sectionCodes: string[],
): Promise<Record<string, string>> {
  const api = (window as any).sharePointAPI;
  const out: Record<string, string> = {};
  if (!api?.loadDocumentListBySection) return out;
  const unique = [...new Set(sectionCodes.filter((c) => c && c.trim()))];
  await Promise.all(
    unique.map(async (code) => {
      try {
        const res = await api.loadDocumentListBySection(code);
        if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
          const name = res.data[0]?.name;
          if (name && String(name).trim()) {
            out[code] = String(name).trim();
          }
        }
      } catch {
        /* ignore per section */
      }
    }),
  );
  return out;
}

export function InformationIPEIUCSection({
  workspaceName,
  workspaceId,
  area,
  documentCards,
  onFilesUploaded,
  onRemoveFile,
  sharePointFiles = [],
  isLoadingSharePointFiles = false,
  existingFiles = {},
}: InformationIPEIUCSectionProps) {
  const [informationEntries, setInformationEntries] = useState<InformationEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [workspacePrefix, setWorkspacePrefix] = useState<string>("");
  const [informationExistingFiles, setInformationExistingFiles] = useState<Record<string, string>>({});
  const [filesFromDbBySection, setFilesFromDbBySection] = useState<Record<string, string>>({});
  const displayEntriesRef = useRef<InformationEntry[]>([]);

  // Get standard document cards for this workspace to check if an information is standard
  const standardDocumentCards =
    documentCards ?? WORKSPACE_DOCUMENT_CARDS[workspaceName] ?? [];

  /** Library may be empty; still show one card per configured standard document for this workspace */
  const displayEntries = useMemo((): InformationEntry[] => {
    const fromApi = informationEntries;
    const seen = new Set(fromApi.map((e) => e.information_name));
    const extras: InformationEntry[] = [];
    for (const card of standardDocumentCards) {
      if (!seen.has(card.informationName)) {
        extras.push({
          information_name: card.informationName,
          workspace: [workspaceName],
          workspace_prefix: [],
          file_name: "",
        });
        seen.add(card.informationName);
      }
    }
    return [...fromApi, ...extras];
  }, [informationEntries, standardDocumentCards, workspaceName]);

  displayEntriesRef.current = displayEntries;

  // Generate section code for information entry
  // Format: <phase name>_<workspace prefix>_<information name>
  // This matches the format used by standard document cards (e.g., "Execution_EBE_PayRegistrar")
  // The section code is used to update the "section" field in db.json when files are linked/uploaded/removed
  // Note: Information name is kept as-is (with spaces preserved) in db.json
  const getSectionCodeForInformation = (informationName: string): string => {
    const prefix = workspacePrefix || workspaceId;
    const phaseName = area === "ConcludingProcedures" ? "ConcludingProcedures" : area;
    // Keep information name as-is (preserve spaces, only remove special chars that aren't spaces)
    const normalizedInfoName = informationName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .trim();
    return `${phaseName}_${prefix}_${normalizedInfoName}`;
  };

  // Check if this is a standard document card
  const isStandardCard = (informationName: string): boolean => {
    // Check if any standard card has this information name
    return standardDocumentCards.some(
      (card) => card.informationName === informationName
    );
  };

  // Get section code - use standard if it exists, otherwise generate new one
  const getSectionCode = (informationName: string): string => {
    if (isStandardCard(informationName)) {
      // Find the standard section code
      const standardCard = standardDocumentCards.find(
        (card) => card.informationName === informationName
      );
      return standardCard?.sectionCode || getSectionCodeForInformation(informationName);
    }
    // For new information cards, use the generated format
    return getSectionCodeForInformation(informationName);
  };

  const loadInformationEntries = async () => {
    setLoading(true);
    try {
      if ((window as any).sharePointAPI?.downloadLibrariesInformation) {
        const result = await (window as any).sharePointAPI.downloadLibrariesInformation();
        if (result?.success && result?.data?.information) {
          // Filter information entries that include this workspace
          const filtered = result.data.information.filter((entry: InformationEntry) =>
            entry.workspace && entry.workspace.includes(workspaceName)
          );
          setInformationEntries(filtered);
        } else {
          setInformationEntries([]);
        }
      } else {
        setInformationEntries([]);
      }
    } catch (error) {
      console.error("Error loading information entries:", error);
      setInformationEntries([]);
    } finally {
      setLoading(false);
    }
  };

  // Load existing files for information cards from Libraries_Information.json
  const loadInformationExistingFiles = async (entries: InformationEntry[]) => {
    try {
      const existingFilesMap: Record<string, string> = {};

      for (const entry of entries) {
        const sectionCode = getSectionCode(entry.information_name);
        if (entry.file_name) {
          existingFilesMap[sectionCode] = entry.file_name;
        }
      }

      setInformationExistingFiles(existingFilesMap);
    } catch (error) {
      console.error("Error loading information existing files:", error);
    }
  };

  // Load information entries from Libraries_Information.json
  useEffect(() => {
    loadInformationEntries();
  }, [workspaceName]);

  // Load existing files for information cards (same way as standard cards)
  useEffect(() => {
    if (displayEntries.length === 0) return;
    // workspacePrefix may still be loading; getSectionCode falls back to workspaceId for codes
    void loadInformationExistingFiles(displayEntries);
  }, [displayEntries, workspacePrefix]);

  // Linked uploads are stored on db.json client entries; Libraries_Information may lag or be empty
  useEffect(() => {
    if (displayEntries.length === 0) {
      setFilesFromDbBySection({});
      return;
    }
    let cancelled = false;
    const codes = displayEntries.map((e) => getSectionCode(e.information_name));
    void (async () => {
      const map = await fetchFileNamesFromDbBySectionCodes(codes);
      if (!cancelled) setFilesFromDbBySection(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [displayEntries, workspacePrefix, standardDocumentCards, workspaceId, area]);

  const refreshFilesFromDb = async () => {
    const entries = displayEntriesRef.current;
    if (entries.length === 0) return;
    const codes = entries.map((e) => getSectionCode(e.information_name));
    const map = await fetchFileNamesFromDbBySectionCodes(codes);
    setFilesFromDbBySection(map);
  };

  // Load workspace prefix on mount
  useEffect(() => {
    const loadPrefix = async () => {
      // Try to get prefix from workspace name (for standard workspaces)
      const prefixFromName = getWorkspacePrefix(workspaceName);
      if (prefixFromName) {
        setWorkspacePrefix(prefixFromName);
        return;
      }
      
      // For custom workspaces, try to get prefix from Sections.json
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            // Check all areas for the workspace
            const areas: Array<"Planning" | "Execution" | "ConcludingProcedures"> = [
              "Planning",
              "Execution",
              "ConcludingProcedures",
            ];
            
            for (const areaKey of areas) {
              if (result.data[areaKey] && result.data[areaKey][workspaceName]) {
                setWorkspacePrefix(result.data[areaKey][workspaceName]);
                return;
              }
            }
          }
        }
      } catch (error) {
        console.warn("Failed to get workspace prefix from Sections.json:", error);
      }
      
      // If not found, use workspaceId as fallback (it's usually the prefix)
      setWorkspacePrefix(workspaceId);
    };
    
    loadPrefix();
  }, [workspaceName, workspaceId]);

  if (loading) {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-white">IPE/IUC</h3>
        <p className="text-sm text-white/50">Loading…</p>
      </div>
    );
  }

  if (displayEntries.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-white">IPE/IUC</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {displayEntries.map((entry) => {
          const sectionCode = getSectionCode(entry.information_name);
          const isStandard = isStandardCard(entry.information_name);
          
          return (
            <Card key={entry.information_name} className="border-white/10 bg-white/5 text-white overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Upload className="h-4 w-4 text-white/60" />
                  <h4 className="text-sm font-semibold">{entry.information_name}</h4>
                </div>
                <div className="overflow-hidden">
                  <SharePointFileUpload
                    onFilesUploaded={async (_files) => {
                      if (onFilesUploaded) {
                        await onFilesUploaded();
                      }
                      await loadInformationEntries();
                      await refreshFilesFromDb();
                    }}
                    onRemoveFile={async () => {
                      if (onRemoveFile) {
                        await onRemoveFile();
                      }
                      await loadInformationEntries();
                      await refreshFilesFromDb();
                    }}
                    multiple={false}
                    accept="*"
                    triggerText="Upload File"
                    showReferenceInput={true}
                    referencePlaceholder="Enter reference"
                    sectionList={[sectionCode]}
                    folderName="client"
                    className="space-y-2"
                    sharePointFiles={sharePointFiles}
                    isLoadingSharePointFiles={isLoadingSharePointFiles}
                    existingFileName={
                      filesFromDbBySection[sectionCode] ||
                      existingFiles[sectionCode] ||
                      informationExistingFiles[sectionCode] ||
                      entry.file_name ||
                      ""
                    }
                    linkFromSharePointMultiple={false}
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
